import {
  BlikPopup,
  CardPopup,
  savePaymentReturn,
  SettleResult,
  getMethodDescription,
  getPaymentIcon,
  hasCardAdapter,
  registerPaymentMethod,
  useAvailableMethods
} from 'payments'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import { ACCOUNT_TYPE, fetchUserData } from '../accountSlice'
import React, { useEffect, useRef, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { useReduxDispatch } from 'store'
import { useHistory } from 'react-router-dom'
import { displayPrice } from 'utils/string'
import Popup from 'components/popups/Popup'
import Loading from 'components/Loading'
import Option from 'components/Option'
import Error from 'components/Error'
import { GET } from 'rest/request'
import * as urls from 'rest/urls'
import 'payments/style.scss'

const REGISTRABLE_METHODS = ['blik', 'online_card', 'online']
const DEFAULT_VERIFICATION_AMOUNT = 1

interface CompanyOptions {
  id: string
  name: string
  methods: payments.PaymentMethod[]
  verificationAmount?: number
}

interface NormalizedOptions {
  companies: CompanyOptions[]
  verificationAmount: number
}

const toList = (value: any): any[] =>
  Array.isArray(value) ? value : (value && typeof value === 'object') ? Object.values(value) : []

const toNumber = (value: any) => {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : undefined
}

/**
 * Kontrakt opisuje odpowiedź słownie („listę firm i metod”), więc przyjmujemy obie sensowne formy:
 * lista firm z metodami albo płaska lista metod z companyId / companyName.
 */
const normalizeOptions = (response: any): NormalizedOptions => {
  const root = response && !Array.isArray(response) && typeof response === 'object' ? response : {}
  const rawCompanies = toList(root.companies ?? root.data ?? response)

  const looksLikeMethods = rawCompanies.length > 0 && rawCompanies.every(item => typeof item?.key === 'string')

  let companies: CompanyOptions[]
  if (looksLikeMethods) {
    const grouped: Record<string, CompanyOptions> = {}
    rawCompanies.forEach(method => {
      const id = String(method.companyId ?? method.company?.id ?? '')
      grouped[id] ??= { id, name: method.companyName ?? method.company?.name ?? '', methods: [] }
      grouped[id].methods.push(method)
    })
    companies = Object.values(grouped)
  } else {
    companies = rawCompanies.map(company => ({
      id: String(company?.id ?? company?.companyId ?? ''),
      name: company?.name ?? company?.companyName ?? company?.shortName ?? '',
      methods: toList(company?.methods ?? company?.paymentMethods),
      verificationAmount: toNumber(company?.verificationAmount)
    }))
  }

  companies = companies
    .map(company => ({
      ...company,
      methods: company.methods
        .filter(method => REGISTRABLE_METHODS.includes(method?.key) && method.supportsRecurring !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    }))
    .filter(company => company.id && company.methods.length > 0)

  return {
    companies,
    verificationAmount: toNumber(root.verificationAmount) ?? DEFAULT_VERIFICATION_AMOUNT
  }
}

type Step = 'choose' | 'blik' | 'card'

const AddPaymentMethodPopup: React.FC = () => {
  const [options, setOptions] = useState<NormalizedOptions | null>(null)
  const [failed, setFailed] = useState(false)
  const [companyId, setCompanyId] = useState('')
  const [methodId, setMethodId] = useState<number | string>('')
  const [step, setStep] = useState<Step>('choose')
  const [isLoading, setLoading] = useState(false)
  const [warning, setWarning] = useState('')
  const [isDone, setDone] = useState(false)
  const isActive = useRef(true)

  const dispatch = useReduxDispatch()
  const { goBack } = useHistory()
  const { t } = useTranslation('account')
  const { t: tp } = useTranslation('payment')

  useEffect(() => {
    isActive.current = true
    GET(urls.paymentmethodsOptions, { credentials: true })
      .then(response => {
        if (!isActive.current) return
        const normalized = normalizeOptions(response)
        setOptions(normalized)
        if (normalized.companies.length === 1) setCompanyId(normalized.companies[0].id)
      })
      .catch(() => {
        if (isActive.current) setFailed(true)
      })
    return () => {
      isActive.current = false
    }
  }, [])

  const company = options?.companies.find(item => item.id === companyId)
  const methods = useAvailableMethods(company?.methods)
  const method = methods?.find(item => item.id === methodId)

  // zmiana wystawcy czyści wybór metody - id metod są per wystawca
  useEffect(() => {
    setMethodId('')
    setWarning('')
  }, [companyId])

  const verificationAmount = company?.verificationAmount ?? options?.verificationAmount ?? DEFAULT_VERIFICATION_AMOUNT
  const note = t('verificationAmountNote').replace('{amount}', displayPrice(verificationAmount))

  const getBody = (extra: Record<string, unknown> = {}) => {
    if (!company || !method) return null
    // popup BLIK oddaje { code }, a rejestracja przyjmuje blikCode
    const { code, ...rest } = extra
    return {
      companyId: company.id,
      method: method.key,
      consent: true,
      ...(code ? { blikCode: code } : {}),
      ...rest
    }
  }

  const finish = (messageKey: string, type: 'success' | 'info') => {
    dispatch(addNotification({ type, translate: false, message: t(messageKey) }))
    dispatch(fetchUserData(ACCOUNT_TYPE.cards))
    setDone(true)
    goBack()
  }

  /** Jedna reakcja na wynik - wspólna dla BLIK-a, karty i przekierowania. */
  const handleResult = (result?: SettleResult) => {
    if (!result) return

    switch (result.type) {
      case 'redirect':
        // bramka / 3DS wraca na /payment/status - stamtąd odsyłamy klienta z powrotem do Konta
        savePaymentReturn('/account/payments', 'paymentMethod')
        setDone(true)
        window.open(result.url, '_self')
        return

      case 'success':
        finish('paymentMethodAdded', 'success')
        return

      case 'pending':
        finish('paymentMethodPending', 'info')
        return

      default:
        setLoading(false)
        if (!result.handled) setWarning(result.message || t('paymentMethodAddFailed'))
    }
  }

  const submit = async () => {
    if (!company || !method || isLoading) return
    setWarning('')

    if (method.key === 'blik') {
      setStep('blik')
      return
    }
    if (method.key === 'online_card' && hasCardAdapter(method.cardForm)) {
      setStep('card')
      return
    }

    // `online` (albo karta bez formularza u nas) - rejestracja bez tokenu, backend oddaje url bramki
    setLoading(true)
    const result = await registerPaymentMethod(getBody(), { isActive: () => isActive.current })
    if (!isActive.current) return
    handleResult(result)
  }

  if (isDone) return null

  if (step === 'blik') {
    return <BlikPopup
      note={note}
      submitText={t('addPaymentMethodSubmit')}
      submit={registerPaymentMethod}
      getBody={getBody}
      onResult={handleResult}
      close={() => setStep('choose')} />
  }

  if (step === 'card' && method?.cardForm) {
    return <CardPopup
      note={note}
      saveForRecurring
      cardForm={method.cardForm}
      submitText={t('addPaymentMethodSubmit')}
      submit={registerPaymentMethod}
      getBody={getBody}
      onResult={handleResult}
      close={() => setStep('choose')} />
  }

  const renderCompanies = () => (
    <div>
      <div className='list__header element__title'>{t('chooseCompany')}</div>
      <div className='pmAddOptions'>
        {options!.companies.map(item => (
          <Option
            key={item.id}
            icon='company'
            title={item.name}
            desc=''
            selected={item.id === companyId}
            secondaryTheme
            actionOnClick
            action={() => setCompanyId(item.id)} />
        ))}
      </div>
    </div>
  )

  const renderMethods = () => (
    <div>
      <div className='list__header element__title'>{t('chooseMethod')}</div>
      <div className='pmAddOptions'>
        {methods && methods.length > 0
          ? methods.map(item => (
            <Option
              key={item.id}
              title={item.name}
              desc={item.key === 'online' ? tp('saveForRecurringOnGateway') : (getMethodDescription(item) || '')}
              icon={getPaymentIcon(item)}
              selected={item.id === methodId}
              secondaryTheme
              actionOnClick
              action={() => {
                setWarning('')
                setMethodId(item.id)
              }} />
          ))
          : <p>{t('noMethodsToAdd')}</p>}
      </div>
    </div>
  )

  return (
    <Popup>
      <div className='account__card'>
        <div className='popup__form form summary pmDetails pmAdd'>
          <div className='popup__title'>
            <div>{t('addPaymentMethodTitle')}</div>
          </div>

          {failed
            ? <Error staticPos />
            : !options
              ? <Loading />
              : options.companies.length === 0
                ? <p>{t('noMethodsToAdd')}</p>
                : <div className='account__list list'>
                  <p className='pmAddDesc'>{t('addPaymentMethodDesc')}</p>

                  {options.companies.length > 1 && renderCompanies()}
                  {company && renderMethods()}

                  <p className='pmAddNote'>
                    <i className='icon icon--info s14' /> 
                    <span>{note}</span>
                  </p>
                  {warning && <p className='--warning'>{warning}</p>}

                  <div>
                    <ArrowBtn
                      enlarged
                      text={t('addPaymentMethodSubmit')}
                      disabled={!method}
                      isLoading={isLoading}
                      action={submit} />
                    <BackBtn
                      text={tp('cancel')}
                      action={goBack} />
                  </div>
                </div>}
        </div>
      </div>
    </Popup>
  )
}

export default AddPaymentMethodPopup
